import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Table,
  TableProps,
  Switch,
  Space,
  ConfigProvider,
  theme,
  Select,
  Button as AntdButton,
  Popconfirm,
  Modal,
  Tag,
  message,
} from "antd";
import { Input } from "@/components/ui/inputForm";
import { DeleteOutlined, EditOutlined, HistoryOutlined, PlusOutlined } from "@ant-design/icons";
import dayjs from "dayjs";
import {
  ativarDesativarProduto,
  deleteProduto,
  getAllProdutos,
  getProdutoHistorico,
} from "@/services/produto";
import { getUnidades } from "@/services/unidade";
import { errorHandler } from "@/utils/errorhandler";
import { usePermissions } from "@/hooks/usePermissions";

const MENU_ID_PRODUTO = 30;

const CORES_ACAO: { [key: string]: string } = {
  "Inclusão": "green",
  "Alteração": "blue",
  "Ativação": "cyan",
  "Inativação": "orange",
  "Exclusão": "red",
};

function unidadeLogadaId(): number | null {
  try {
    const unidade = JSON.parse(localStorage.getItem("UnidadeLogada") as any);
    return unidade?.id ?? null;
  } catch {
    return null;
  }
}

export default function ConsultaProduto() {
  const [produtos, setProdutos] = useState<any[]>([]);
  const [unidades, setUnidades] = useState<any[]>([]);
  const [unidadesSelecionadas, setUnidadesSelecionadas] = useState<number[]>([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("todos");
  const [historico, setHistorico] = useState<any[]>([]);
  const [historicoProduto, setHistoricoProduto] = useState<any | null>(null);
  const navigate = useNavigate();
  const { canInsert, canEdit } = usePermissions();

  useEffect(() => {
    const carregar = async () => {
      try {
        const [produtosResponse, unidadesResponse] = await Promise.all([
          getAllProdutos(),
          getUnidades(),
        ]);
        if (produtosResponse.status === 200) {
          setProdutos(produtosResponse.data);
        }
        const unidadesAtivas = (unidadesResponse.data || []).filter((u: any) => u.ativo ?? true);
        setUnidades(unidadesAtivas);

        // O filtro inicia na unidade logada; selecionar mais unidades mostra
        // valor/estoque de cada uma.
        const logadaId = unidadeLogadaId();
        if (logadaId && unidadesAtivas.some((u: any) => u.id === logadaId)) {
          setUnidadesSelecionadas([logadaId]);
        } else if (unidadesAtivas.length > 0) {
          setUnidadesSelecionadas([unidadesAtivas[0].id]);
        }
      } catch (error) {
        errorHandler(error);
      }
    };
    carregar();
  }, []);

  const handleGetProdutos = async () => {
    try {
      const response = await getAllProdutos();
      if (response.status === 200) {
        setProdutos(response.data);
      }
    } catch (error) {
      errorHandler(error);
    }
  };

  const handleAdd = useCallback(() => {
    navigate("/produto/cadastro");
  }, [navigate]);

  const handleEdit = (id: number) => {
    navigate(`/produto/${id}`);
  };

  const toggleAtivo = async (produto: any) => {
    try {
      await ativarDesativarProduto(produto.id, !produto.ativo);
      setProdutos((prev) =>
        prev.map((p) => (p.id === produto.id ? { ...p, ativo: !p.ativo } : p))
      );
      message.success(`Produto ${!produto.ativo ? "ativado" : "inativado"} com sucesso!`);
    } catch (error) {
      errorHandler(error);
    }
  };

  const excluirProduto = async (id: number) => {
    try {
      await deleteProduto(id);
      message.success("Produto excluído com sucesso!");
      await handleGetProdutos();
    } catch (error) {
      errorHandler(error);
    }
  };

  const abrirHistorico = async (produto: any) => {
    try {
      const response = await getProdutoHistorico(produto.id);
      setHistorico(response.data);
      setHistoricoProduto(produto);
    } catch (error) {
      errorHandler(error);
    }
  };

  const filteredProdutos = produtos.filter((p) => {
    const matchesSearch = p.nome?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus =
      statusFilter === "todos" ||
      (statusFilter === "ativos" && p.ativo) ||
      (statusFilter === "inativos" && !p.ativo);
    return matchesSearch && matchesStatus;
  });

  const configuracaoDaUnidade = (produto: any, unidadeId: number) =>
    produto.unidades?.find((u: any) => u.unidadeId === unidadeId);

  const nomeDaUnidade = (unidadeId: number) =>
    unidades.find((u) => u.id === unidadeId)?.nome ?? `Unidade ${unidadeId}`;

  // Colunas fixas Unidade/Valor/Estoque: a célula da linha do produto quebra em
  // uma sublinha por unidade selecionada, alinhada entre as três colunas.
  const linhasDaColuna = (
    record: any,
    conteudo: (unidadeId: number) => string
  ) => (
    <div className="flex flex-col">
      {unidadesSelecionadas.map((unidadeId) => (
        <span key={unidadeId} className="leading-6 whitespace-nowrap">
          {conteudo(unidadeId)}
        </span>
      ))}
    </div>
  );

  const colunasPorUnidade: TableProps<any>["columns"] = [
    {
      title: "Unidade",
      key: "unidade",
      render: (_: any, record: any) => linhasDaColuna(record, (unidadeId) => nomeDaUnidade(unidadeId)),
    },
    {
      title: "Valor",
      key: "valor",
      render: (_: any, record: any) =>
        linhasDaColuna(record, (unidadeId) => {
          const configuracao = configuracaoDaUnidade(record, unidadeId);
          return configuracao != null
            ? configuracao.valor.toLocaleString("pt-BR", { style: "currency", currency: "BRL" })
            : "-";
        }),
    },
    {
      title: "Estoque",
      key: "estoque",
      render: (_: any, record: any) =>
        linhasDaColuna(record, (unidadeId) => {
          const configuracao = configuracaoDaUnidade(record, unidadeId);
          return configuracao != null
            ? Number(configuracao.quantidadeEstoque).toLocaleString("pt-BR")
            : "-";
        }),
    },
  ];

  const columns: TableProps<any>["columns"] = [
    {
      title: "Nome",
      dataIndex: "nome",
      key: "nome",
    },
    {
      title: "Descrição",
      dataIndex: "descricao",
      key: "descricao",
      ellipsis: true,
      render: (value) => value || "-",
    },
    {
      title: "Categoria",
      dataIndex: "categoriaProdutoNome",
      key: "categoriaProdutoNome",
      render: (value) => value || "-",
    },
    ...(colunasPorUnidade ?? []),
    {
      title: "Status",
      dataIndex: "ativo",
      key: "ativo",
      render: (value, record) => (
        <Switch
          checked={!!value}
          onChange={() => toggleAtivo(record)}
          checkedChildren="Ativo"
          unCheckedChildren="Inativo"
          disabled={!canEdit(MENU_ID_PRODUTO)}
        />
      ),
    },
    {
      title: "Ação",
      key: "acao",
      render: (_, record) => (
        <Space size="middle">
          {canEdit(MENU_ID_PRODUTO) && (
            <AntdButton
              type="text"
              icon={<EditOutlined />}
              title="Editar produto"
              onClick={() => handleEdit(record.id)}
            />
          )}
          <AntdButton
            type="text"
            icon={<HistoryOutlined />}
            title="Histórico do produto"
            onClick={() => abrirHistorico(record)}
          />
          {canEdit(MENU_ID_PRODUTO) && (
            <Popconfirm
              title="Confirma a exclusão do produto?"
              description="Os valores e estoques por unidade também serão apagados. Esta ação não pode ser desfeita."
              onConfirm={() => excluirProduto(record.id)}
              okText="Sim"
              cancelText="Não"
            >
              <AntdButton type="text" danger icon={<DeleteOutlined />} title="Excluir produto" />
            </Popconfirm>
          )}
        </Space>
      ),
    },
  ];

  const historicoColumns: TableProps<any>["columns"] = [
    {
      title: "Data",
      dataIndex: "dataRegistro",
      key: "dataRegistro",
      render: (value) => (value ? dayjs(value).format("DD/MM/YYYY HH:mm") : "-"),
    },
    {
      title: "Ação",
      dataIndex: "acao",
      key: "acao",
      render: (value) => <Tag color={CORES_ACAO[value] || "default"}>{value}</Tag>,
    },
    {
      title: "Usuário",
      dataIndex: "usuarioNome",
      key: "usuarioNome",
    },
  ];

  return (
    <>
      <div className="mb-8">
        <span className="text-white text-2xl">Consulta de Produtos</span>
      </div>
      <ConfigProvider theme={{ algorithm: [theme.compactAlgorithm, theme.darkAlgorithm] }}>
        <div className="flex gap-8 items-end mb-8">
          <Input
            type="text"
            placeholder="Pesquisar por nome"
            className="w-80"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
          <Select
            mode="multiple"
            allowClear
            placeholder="Unidades"
            aria-label="Unidades"
            value={unidadesSelecionadas}
            onChange={setUnidadesSelecionadas}
            style={{ minWidth: 240 }}
            options={unidades.map((u) => ({ value: u.id, label: u.nome }))}
          />
          <Select
            value={statusFilter}
            onChange={setStatusFilter}
            style={{ width: 160 }}
            options={[
              { value: "todos", label: "Todos" },
              { value: "ativos", label: "Ativos" },
              { value: "inativos", label: "Inativos" },
            ]}
          />
          {canInsert(MENU_ID_PRODUTO) && (
            <AntdButton className="flex gap-2" onClick={handleAdd}>
              Novo
              <PlusOutlined />
            </AntdButton>
          )}
        </div>
        <div className="grid">
          <div>
            <Table columns={columns} dataSource={filteredProdutos} rowKey="id" />
          </div>
        </div>
        <Modal
          title={`Histórico — ${historicoProduto?.nome ?? ""}`}
          open={!!historicoProduto}
          onCancel={() => setHistoricoProduto(null)}
          footer={null}
          width={640}
        >
          <Table
            columns={historicoColumns}
            dataSource={historico}
            rowKey="id"
            pagination={false}
            size="small"
          />
        </Modal>
      </ConfigProvider>
    </>
  );
}
